import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { scrapersApi } from '@/lib/api/scrapers.api';

import type {
  CreateScraperDto,
  ScraperImportDto,
  ScraperQuery,
  TestScraperDto,
  UpdateScraperDto,
} from '@repo/shared-types';

const KEY = ['scrapers'];

export function useScrapers(query?: ScraperQuery) {
  return useQuery({
    queryKey: [...KEY, query],
    queryFn: () => scrapersApi.getAll(query),
  });
}

export function useCreateScraper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateScraperDto) => scrapersApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateScraper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScraperDto }) =>
      scrapersApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteScraper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scrapersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useTestScraper() {
  return useMutation({
    mutationFn: (data: TestScraperDto) => scrapersApi.test(data),
  });
}

export function useRunScraper() {
  return useMutation({
    mutationFn: ({ id, args }: { id: string; args?: Record<string, unknown> }) =>
      scrapersApi.run(id, args ?? {}),
  });
}

export function useImportScrapers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ScraperImportDto) => scrapersApi.import(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
